/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { transaction, observable } from 'mobx';
import EmptyModel, { proxyHandler } from '@src/slide/domain/ui-model/element-modeles/empty-model';
import MultiplyModel from '@src/slide/domain/ui-model/element-modeles/multiply-model';
import {
    enumerateGroup,
    enumerateGroupItems,
} from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';

class SelectedEleManager {
    @observable selectedArrayDic = {
        [SelectedEleMgrType.SLIDE]: [], // Slide 内的元素
        [SelectedEleMgrType.GROUP]: [], // Group 内的元素
        [SelectedEleMgrType.SMART_ART]: [], // SmartArt 内的元素
    };

    private selectionService: ISelectionService;

    get selectedArray() {
        if (this.selectedArrayForType(SelectedEleMgrType.GROUP).length > 0) {
            return this.selectedArrayForType(SelectedEleMgrType.GROUP);
        }
        if (this.selectedArrayForType(SelectedEleMgrType.SMART_ART).length > 0) {
            return this.selectedArrayForType(SelectedEleMgrType.SMART_ART);
        }
        if (this.selectedArrayForType(SelectedEleMgrType.SLIDE).length > 0) {
            return this.selectedArrayForType(SelectedEleMgrType.SLIDE);
        }
        return null;
    }

    get length() {
        return this.selectedArray?.length || 0;
    }

    constructor(selection: ISelectionService) {
        this.selectionService = selection;
    }

    /**
     * 查找元素的顶层groupShape
     * @param item
     * @returns {*}
     */
    findRootGroup(item) {
        let { parent } = item;
        enumerateGroup(item, (groupModel) => {
            parent = groupModel;
        });
        return parent;
    }

    /**
     * 要add的slide是groupArr中元素的祖先group（此种场景就不能清空groupArr）
     * 比如页面无选中元素时，点击某组合内的元素。此时会先将子元素加入groupArr，再讲父元素加入slideArr。
     * 前置信息：groupAr中的元素永远都只会是一个groupShape下的
     * @param groupChildren group列表
     * @param slideModel 要add的slide
     */
    isRootGroup(groupChildren, slideModel) {
        return groupChildren.length >= 1 && this.findRootGroup(groupChildren[0]) === slideModel;
    }

    /**
     * 要add的group后代元素就是当前选中的祖先group的后代（此种场景就允许add）
     * @param slideChildren slide列表
     * @param groupChild 要add的groupChild
     */
    isElementInGroup(slideChildren, groupChild) {
        return slideChildren.length === 1 && this.findRootGroup(groupChild) === slideChildren[0];
    }

    /**
     * 是否为不能增加到选中队列的元素
     * @param item
     * @returns {boolean|*}
     */
    isIgnoreItem(item) {
        return !item || this._findItem(item) || item.hidden || !item.selectable;
    }

    add(...items: Array<IElementBaseModel>) {
        transaction(() => {
            items.forEach((item) => {
                // 元素不存在||此元素已处于选中状态 则忽略
                if (this.isIgnoreItem(item)) {
                    return;
                }
                // 组合的后代元素不可与组合外的元素同时处于选中态，所以需要做以下判断
                const type = this._kind(item);
                const groupChildren = this.selectedArrayForType(SelectedEleMgrType.GROUP) || [];
                const slideChildren = this.selectedArrayForType(SelectedEleMgrType.SLIDE) || [];
                const smartArtChildren = this.selectedArrayForType(SelectedEleMgrType.SMART_ART) || [];
                // add的是非组合后代元素（如果组合后代元素有选中项，因为互斥原则需要将组合的所有后代从选中列表移除）
                if (
                    type === SelectedEleMgrType.SLIDE
                    && !this.isRootGroup(groupChildren, item)
                ) {
                    this.remove(...groupChildren);
                }
                // add的是组合后代元素（如果当前选中列表中没有非组合后代元素则直接继续，如果有则因为互斥原则不允许此元素add进来）
                if (
                    type === SelectedEleMgrType.GROUP
                    && slideChildren.length
                    && !this.isElementInGroup(slideChildren, item)
                ) {
                    return;
                }
                // 将此元素加入选中列表
                item.selected = true;
                this.selectedArrayForType(type).push(item);
            });
        });
    }

    remove(...items: Array<IElementBaseModel>) {
        transaction(() => {
            items.forEach((item) => {
                const selectedArray = this.selectedArrayForType(this._kind(item));
                const index = selectedArray.indexOf(item);
                if (index !== -1) {
                    item.selected = false;
                    selectedArray.splice(index, 1);
                }
            });
        });
    }

    public selectedArrayForType(type): Array<IElementBaseModel> {
        return this.selectedArrayDic[type];
    }

    /**
     *
     * @param type SelectedEleMgrType
     */
    clear(type = '') {
        transaction(() => {
            if (type) {
                const selectedArray = this.selectedArrayDic[type];
                selectedArray.forEach((item: IElementBaseModel) => {
                    item.selected = false;
                });
                selectedArray.splice(0, selectedArray.length);
            } else {
                const keys = Object.keys(this.selectedArrayDic);
                keys.forEach((key) => {
                    const selectedArray = this.selectedArrayDic[key];
                    selectedArray.forEach((item: IElementBaseModel) => {
                        item.selected = false;
                    });
                    selectedArray.splice(0, selectedArray.length);
                });
            }
        });
    }

    // Private Methods

    _kind(item) {
        let { parent } = item;
        enumerateGroup(item, (groupModel) => {
            parent = groupModel;
        });

        switch (parent.type) {
            case UIModelType.SLIDE:
            case UIModelType.MASTER:
            case UIModelType.LAYOUT:
                return SelectedEleMgrType.SLIDE;
            case UIModelType.GROUP:
                return SelectedEleMgrType.GROUP;
            case UIModelType.SMART_ART:
                return SelectedEleMgrType.SMART_ART;
            default:
                throw new Error(' SelectedEleManager item Type Error !');
        }
    }

    _findItem(item) {
        const selectedArray = this.selectedArrayForType(this._kind(item));
        return selectedArray.indexOf(item) !== -1;
    }

    getCurrentModel() {
        let model: any = null;

        // 当前在note区的话，返回note，此处逻辑与qiyuan讨论过，后续selectedManager统一后也应当统一
        if (this.selectionService.lastMouseDownRegion === RegionType.NOTE && this.length === 0) {
            model = this.selectionService.currentSlide?.noteData;
            if (model) return model;
        }

        const len = this.length;

        if (len === 0) {
            model = new EmptyModel({});
            model = new Proxy(model, proxyHandler);
        } else if (len === 1) {
            model = this.selectionService.selectedElements.selectedArray?.[0];
        } else {
            model = new MultiplyModel({
                selectedModels: this.selectedArray || [],
                selectionService: this.selectionService,
            });
        }

        return model;
    }

    public getSortedSelectedArray(): IElementBaseModel[] {
        if (!this.selectedArray || this.selectedArray.length === 0) {
            return [];
        }
        let children: IElementBaseModel[];
        const slideChildren = this.selectedArrayForType(SelectedEleMgrType.SLIDE) || [];
        const firstElement = this.selectedArray[0];
        if (this.isElementInGroup(slideChildren, firstElement)) {
            children = [];
            // 如果第一个元素为组合子元素，遍历组合，按层级顺序将所有子元素添加到数组中
            const rootGroup = this.findRootGroup(firstElement);
            enumerateGroupItems(rootGroup, (child) => {
                children.push(child);
            });
        } else {
            children = firstElement.parent?.children as IElementBaseModel[];
        }
        const sortedArray: IElementBaseModel[] = [];
        this.selectedArray.forEach((item) => {
            this.addElementByLevel(sortedArray, children, item);
        });

        return sortedArray;
    }

    /**
     * 按照层级顺序添加元素
     * @param sortedArray
     * @param children
     * @param item
     * @private
     */
    private addElementByLevel(
        sortedArray: IElementBaseModel[],
        children: IElementBaseModel[],
        item: IElementBaseModel,
    ) {
        const len = sortedArray.length;
        if (len === 0) {
            sortedArray.push(item);
            return;
        }

        let left = 0;
        let right = len - 1;
        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const ele = sortedArray[mid];
            const eleIndex = children?.indexOf?.(ele) ?? -1;
            const itemIndex = children?.indexOf?.(item) ?? -1;

            if (itemIndex < eleIndex) {
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        sortedArray.splice(left, 0, item);
    }
}

export default SelectedEleManager;
